/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { formatValue, type CellFormat } from "../format";
import { resolveMetricValue } from "@app/reports-v2/metrics";

type MetricSpec = {
  key: string;
  labelKey: string;
  format: CellFormat;
  emphasis?: boolean;
  descriptionKey?: string;
  ratioOf?: string;
  sourceKey?: string;
  section?: string;
};

export const ReportSummary = defineComponent({
  name: "DataRoomReportSummary",
  props: {
    spec: { type: Object, required: true },
    summary: { type: Object, default: null },
    currency: { type: String, default: "CHF" },
    loading: { type: Boolean, default: false },
  },
  setup(props: any) {
    const { t } = useLocalization();
    /**
     * Ratio metrics are derived client-side so SQL keeps returning raw values.
     * Emitted as a real percentage (0-100) to match the `percentage` formatter.
     */
    const resolve = (m: MetricSpec, metrics: Record<string, unknown>) => {
      if (!m.ratioOf) return metrics[m.key];
      const numeratorKey = m.sourceKey ?? m.key;
      const denominator = Math.abs(Number(metrics[m.ratioOf]) || 0);
      if (denominator === 0) return null;
      return (Math.abs(Number(metrics[numeratorKey]) || 0) / denominator) * 100;
    };

    const renderCard = (m: MetricSpec, metrics: Record<string, unknown>) => {
      // Shared with the PDF view model and the XLSX workbook — see
      // resolveMetricValue in reports-v2/contracts. Ratio metrics are derived at
      // render time so SQL keeps returning raw values only.
      const value = resolveMetricValue(m, metrics);
      const display = formatValue(value, m.format, props.currency);

      // A zero should not wear the accent — an emphasised "CHF 0" reads as a
      // real figure at a glance when it is really an empty period.
      const isZero = !props.loading && !/[1-9]/.test(display);

      return (
        <div
          key={m.key}
          class={[
            // Every tile gets a shadow now. Supporting tiles previously had
            // none and a 60%-opacity border, which made them read as flat
            // patches of background rather than cards.
            "relative flex min-h-[88px] flex-col justify-between overflow-hidden rounded-xl bg-base-100 p-3 pl-4 shadow-sm sm:min-h-[96px] sm:p-4 sm:pl-5",
          ]}
        >
          {/* Headline metrics get a coloured spine; supporting ones a quiet
              one. Cheaper than tinting the whole card, which washes out the
              number the tile exists to show. */}
          <span
            class={[
              "absolute inset-y-0 left-0 w-[3px]",
              m.emphasis ? "bg-[#2f7ded]" : "bg-base-content/25",
            ]}
          />
          <div class="flex items-start justify-between gap-1">
            <span class="text-[10px] font-semibold uppercase tracking-wider text-base-content sm:text-[11px]">
              {t(m.labelKey)}
            </span>
            {m.descriptionKey && (
              <span
                class="material-symbols-outlined shrink-0 cursor-help text-[14px] leading-none text-base-content/55 hover:text-primary"
                title={t(m.descriptionKey)}
              >
                info
              </span>
            )}
          </div>
          <div
            class={[
              "mt-2 font-bold leading-tight tabular-nums",
              m.emphasis ? "text-xl sm:text-2xl" : "text-lg sm:text-xl",
              isZero ? "text-accent" : "text-accent",
            ]}
            title={props.loading ? undefined : display}
          >
            {props.loading ? (
              <span class="inline-block h-7 w-24 animate-pulse rounded-lg bg-base-300" />
            ) : (
              display
            )}
          </div>
        </div>
      );
    };

    return () => {
      const metrics: MetricSpec[] = props.spec?.metrics ?? [];
      const metricsObj =
        props.summary?.[props.spec?.metricsPath ?? "metrics"] ?? {};

      // Headline metrics lead; supporting metrics follow. A KPI block should read
      // as a story, not an undifferentiated wall of numbers.
      const sections = metrics.reduce<
        Array<{ key: string | undefined; items: MetricSpec[] }>
      >((acc, m) => {
        const last = acc[acc.length - 1];
        if (last && last.key === m.section) last.items.push(m);
        else acc.push({ key: m.section, items: [m] });
        return acc;
      }, []);

      return (
        <div class="flex flex-col gap-4">
          {sections.map((section, i) => (
            <section key={section.key ?? i}>
              {section.key && (
                <h4 class="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
                  {t(section.key)}
                </h4>
              )}
              {/* Consistent column count across every section, so cards are the
                  same width everywhere and simply wrap. A section with fewer
                  cards leaves the trailing columns empty (left-aligned) rather
                  than stretching its cards to fill the row. */}
              <div class="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
                {section.items.map((m) => renderCard(m, metricsObj))}
              </div>
            </section>
          ))}
        </div>
      );
    };
  },
});
